import { MemoryMessageToSend } from "@/components/chat/chat-conversation";
import { ChatConversationSkeleton } from "@/components/chat/chat-conversation-skeleton";
import { ChatLayout } from "@/components/chat/chat-layout";
import { Button } from "@/components/ui/button";
import {
  Empty,
  EmptyContent,
  EmptyHeader,
  EmptyTitle,
} from "@/components/ui/empty";
import { useToastError } from "@/components/ui/use-toast";
import { useChatStream } from "@/contexts/chat-stream-context";
import { useTeams } from "@/contexts/teams-provider";
import { useApi } from "@/hooks/useApi";
import { useApiQuery } from "@/hooks/useApiQuery";
import { useChatMessageListener } from "@/hooks/useChatActions";
import { conversationsListKey } from "@/hooks/useConversationsList";
import { useLlmSelection } from "@/hooks/useLlmSelection";
import { useIsSlow } from "@/hooks/useIsSlow";
import { useMobileHeader } from "@/hooks/useMobileHeader";
import { useTranslation } from "@/hooks/useTranslation";
import { getApiErrorStatus } from "@/lib/api-error";
import { isOffline } from "@/lib/network";
import { setWindowTitle } from "@/lib/window-title";
import type { MobileChatLocationState } from "@/LayoutMobile";
import {
  companyOnShowId,
  companyPreviewPanelStore,
} from "@/stores/company-preview-panel-store";
import { ConversationDTO } from "@shaarp/common";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

/**
 * How long the welcome screen waits for a conversation to be created before
 * giving the message back: the request is the only thing between the user and
 * an answer, and axios never times out by itself.
 */
export const CONVERSATION_CREATION_TIMEOUT_MS = 20_000;

/** Cache key of one conversation, as the mobile chat reads it. */
const conversationKey = (teamId: string | undefined, id: string | undefined) =>
  ["conversation", teamId, id] as const;

/**
 * A message on its way, and the conversation it belongs to: `null` while the
 * conversation it starts is still being created on the welcome screen.
 */
type PendingMessage = {
  readonly message: MemoryMessageToSend;
  readonly conversationId: string | null;
  /**
   * Started on the welcome screen. The router's navigation to the new
   * conversation lands in a transition, a render after the pending message
   * names it: until then the welcome screen keeps showing it as being created.
   */
  readonly fromWelcome?: boolean;
};

/**
 * Messages whose conversation is not on screen any more by the time they can
 * be sent: it was created after the user left the welcome screen, a stream
 * was still stopping when they left, or they left before it went out. Sent
 * when their conversation is next opened, one after the other in the order
 * they were asked, rather than leaving a titled, empty conversation in the
 * sidebar. Module state, so it outlives the page; a reload drops it, as it
 * drops a message being typed. Each is a question the user asked, and the
 * conversation it starts was titled from it: it is sent however late.
 */
const messagesAwaitingTheirConversation = new Map<
  string,
  MemoryMessageToSend[]
>();

/** Queues a message after those already waiting for its conversation. */
const parkMessage = (conversationId: string, message: MemoryMessageToSend) =>
  messagesAwaitingTheirConversation.set(conversationId, [
    ...(messagesAwaitingTheirConversation.get(conversationId) ?? []),
    message,
  ]);

/** Puts back, first in line, a message that was about to go out. */
const parkMessageFirst = (
  conversationId: string,
  message: MemoryMessageToSend
) =>
  messagesAwaitingTheirConversation.set(conversationId, [
    message,
    ...(messagesAwaitingTheirConversation.get(conversationId) ?? []),
  ]);

/** The next message waiting for this conversation, taken out. */
const takeParkedMessage = (
  conversationId: string
): MemoryMessageToSend | null => {
  const [message, ...rest] =
    messagesAwaitingTheirConversation.get(conversationId) ?? [];
  if (rest.length > 0) messagesAwaitingTheirConversation.set(conversationId, rest);
  else messagesAwaitingTheirConversation.delete(conversationId);
  return message ?? null;
};

/**
 * The mobile chat, as in the Claude app: `/chat` opens on the welcome screen —
 * the last conversation no longer opens by itself — and `/chat/:id` on that
 * conversation. The conversation is in the URL, so the sidebar, a deep link and
 * the back gesture all agree on which one is open.
 */
export default function ChatMobilePage() {
  const { conversationId } = useParams();
  const { pathname, key: locationKey, state: locationState } = useLocation();
  const { t } = useTranslation();
  const api = useApi();
  const navigate = useNavigate();
  const toastError = useToastError();
  const queryClient = useQueryClient();
  const { teamSelected } = useTeams();
  const teamId = teamSelected?.id;
  const { llmSelection } = useLlmSelection();
  const { stopStreamBeforeSend } = useChatStream();
  const [pending, setPending] = useState<PendingMessage | null>(null);
  // Read by the question listener, which runs after a stream has stopped.
  const pendingRef = useRef(pending);
  pendingRef.current = pending;
  // A message the welcome screen gives back to its composer: the creation it
  // started was stopped, timed out or failed. Given back wherever the user is
  // by then — only the welcome screen's composer takes it, so it waits there
  // — and let go once restored. Counted, so the same text given back twice is
  // restored twice.
  const [draftToRestore, setDraftToRestore] = useState<{
    readonly text: string;
    readonly attempt: number;
  } | null>(null);
  const giveBack = (message: MemoryMessageToSend) =>
    setDraftToRestore((current) => ({
      text: message.text,
      attempt: (current?.attempt ?? 0) + 1,
    }));
  // The creation in flight, which Stop and the timeout abort, and the
  // questions asked off the chat while it was: they go into the conversation
  // it creates, after its first message, rather than starting another one.
  const creationRef = useRef<{
    readonly controller: AbortController;
    isStoppedByUser: boolean;
    readonly followUps: MemoryMessageToSend[];
  } | null>(null);

  // Where the user is when a creation comes back: they may have left the
  // welcome screen for another conversation meanwhile, or the chat altogether
  // — the router still navigates after the page is gone.
  const pathnameRef = useRef(pathname);
  pathnameRef.current = pathname;
  // The welcome screen's own history entry. An overlay's entry copies it, so
  // opening and closing the sidebar keeps it; "New conversation" from another
  // conversation is a new one.
  const locationKeyRef = useRef(locationKey);
  locationKeyRef.current = locationKey;
  const isMountedRef = useRef(false);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  // The conversation on screen once a stream has stopped, not when the
  // question was asked: the user may have switched in between.
  const conversationIdRef = useRef(conversationId);
  conversationIdRef.current = conversationId;
  // The company a question asked off the chat was about (`LayoutMobile`).
  const locationCompanyIdRef = useRef<string | undefined>(undefined);
  locationCompanyIdRef.current = (
    locationState as MobileChatLocationState | null
  )?.companyIdTarget;

  const {
    data: conversation,
    isError,
    error,
    refetch,
    isFetching,
  } = useApiQuery<ConversationDTO>({
    key: conversationKey(teamId, conversationId),
    path: `/conversation/${conversationId}`,
    enabled: !!conversationId && !!teamId,
    // Seeded by the creation: read again only when invalidated (a new title
    // after the first answer), not on the navigation that follows it.
    staleTime: 30_000,
  });
  // Whether the conversation on show is read yet, for the question listener.
  const isConversationReadRef = useRef(false);
  isConversationReadRef.current = !!conversation;

  // A conversation that is gone — deleted, or another team's after a team
  // switch — leaves for the welcome screen without an error: there is nothing
  // the user can do about it. Any other failure (no network, a 5xx) keeps the
  // conversation's place in history and says so on the page, with a retry —
  // nothing would read it again by itself. A refresh that fails after an
  // answer keeps the conversation on screen.
  const errorStatus = isError ? getApiErrorStatus(error) : null;
  const isGone = errorStatus === 403 || errorStatus === 404;
  useEffect(() => {
    if (!isGone || conversation || !conversationId) return;
    // Deleted: a message left waiting for it has nowhere to go any more, and
    // the sidebar must stop listing it. A 403 is another team's — after a
    // switch — and the message waits for the switch back.
    if (errorStatus === 404) {
      messagesAwaitingTheirConversation.delete(conversationId);
      void queryClient.invalidateQueries({
        queryKey: conversationsListKey(teamId),
      });
    }
    navigate("/chat", { replace: true });
  }, [
    isGone,
    errorStatus,
    conversation,
    conversationId,
    navigate,
    queryClient,
    teamId,
  ]);
  const cannotBeRead = !!conversationId && !conversation && isError && !isGone;

  const title = conversationId
    ? conversation
      ? conversation.title || t("components.mobileShell.untitledConversation")
      : undefined
    : undefined;
  // A wait the user can see: the creation, or a conversation being read.
  const isConnectionSlow = useIsSlow(
    pending?.conversationId === null ||
      (!!conversationId && !conversation && !isError)
  );
  // No sidebar while a conversation is being created: its history entry would
  // take the replace that moves the welcome screen to the new conversation.
  // The creation is bounded and can be stopped, so this never outlives it.
  useMobileHeader({
    title,
    isMenuDisabled: pending?.conversationId === null,
    isConnectionSlow,
  });

  useEffect(() => {
    setWindowTitle(
      conversation?.title
        ? t("chat.conversation.windowTitle", { title: conversation.title })
        : t("chat.conversation.windowTitleDefault")
    );
  }, [conversation?.title, t]);

  // Another conversation, or a new one, does not open under a company panel
  // left over from the previous one's mentions.
  useEffect(() => {
    if (companyPreviewPanelStore.getSnapshot()) {
      companyPreviewPanelStore.close();
    }
  }, [conversationId]);

  const refreshConversations = () =>
    queryClient.invalidateQueries({ queryKey: conversationsListKey(teamId) });

  /** Still on the welcome screen a message was sent from. */
  const isStillOnWelcome = (sentFrom: string) =>
    isMountedRef.current &&
    pathnameRef.current === "/chat" &&
    locationKeyRef.current === sentFrom;

  const createConversation = async (
    message: MemoryMessageToSend,
    companyIdTarget?: string
  ) => {
    // Bound to fail: the message stays where the user wrote it.
    if (isOffline()) {
      giveBack(message);
      toastError({ description: t("components.mobileShell.creationOffline") });
      return;
    }
    const sentFrom = locationKeyRef.current;
    const creation = {
      controller: new AbortController(),
      isStoppedByUser: false,
      followUps: [] as MemoryMessageToSend[],
    };
    creationRef.current = creation;
    const timeout = window.setTimeout(
      () => creation.controller.abort(),
      CONVERSATION_CREATION_TIMEOUT_MS
    );
    // Shown at once while the conversation is created.
    setPending({ message, conversationId: null });
    try {
      const { data } = await api.post<ConversationDTO>(
        "/conversation",
        {
          llmId: message.llmId || llmSelection,
          message: message.text || null,
          ...(companyIdTarget && { companyIdTarget }),
        },
        { signal: creation.controller.signal }
      );
      queryClient.setQueryData(conversationKey(teamId, data.id), data);
      void refreshConversations();
      // Sent after the first, once it is: they wait in its queue.
      creation.followUps.forEach((followUp) => parkMessage(data.id, followUp));
      // The user opened another conversation, started another one, or left
      // the chat meanwhile: the new one waits in the sidebar with its message,
      // sent when it is opened — never into whatever is on screen.
      if (!isStillOnWelcome(sentFrom)) {
        parkMessage(data.id, message);
        setPending(null);
        return;
      }
      setPending({ message, conversationId: data.id, fromWelcome: true });
      // Replaces the welcome screen: back from the conversation leaves the
      // chat instead of landing on an empty composer.
      navigate(`/chat/${data.id}`, { replace: true });
    } catch {
      setPending(null);
      // Every question asked goes back in the field, the first one first:
      // none exists anywhere else.
      giveBack({
        ...message,
        text: [message, ...creation.followUps]
          .map((asked) => asked.text)
          .join("\n\n"),
      });
      // Aborted, the server may still create it after giving up on it: the
      // list is read again now and once more later, so a conversation that
      // lands late shows up in the sidebar rather than nowhere.
      void refreshConversations();
      if (creation.controller.signal.aborted) {
        window.setTimeout(
          () => void refreshConversations(),
          CONVERSATION_CREATION_TIMEOUT_MS
        );
      }
      if (creation.isStoppedByUser) return;
      toastError({
        description: t(
          creation.controller.signal.aborted
            ? "components.mobileShell.creationTimedOut"
            : "components.mobileShell.creationFailed"
        ),
      });
    } finally {
      window.clearTimeout(timeout);
      if (creationRef.current === creation) creationRef.current = null;
    }
  };

  /** Stop on the creation's composer: back to the welcome, text and all. */
  const stopCreating = () => {
    const creation = creationRef.current;
    if (!creation) return;
    creation.isStoppedByUser = true;
    creation.controller.abort();
  };

  // A question asked from elsewhere — a company sheet's quick prompt — goes
  // into the conversation on screen, or starts one from the welcome screen.
  useChatMessageListener((text, prompt) => {
    // Asked from a company's panel, or from a screen about one: a conversation
    // it starts targets that company.
    const companyIdTarget =
      companyOnShowId(companyPreviewPanelStore.getSnapshot()) ??
      locationCompanyIdRef.current;
    companyPreviewPanelStore.close();
    void stopStreamBeforeSend().then(() => {
      const message: MemoryMessageToSend = {
        llmId: llmSelection ?? "auto",
        text,
        filesAttached: [],
        prompt,
      };
      const target = conversationIdRef.current;
      // Not on screen (left), or not read yet (opened meanwhile): handed over
      // at mount, the message would read as the first of a conversation just
      // created and its history would never load. It waits for it.
      // One already on its way: this one goes after it, not over it.
      if (
        target &&
        (!isMountedRef.current ||
          !isConversationReadRef.current ||
          pendingRef.current)
      ) {
        parkMessage(target, message);
      } else if (target) {
        setPending({ message, conversationId: target });
      } else if (creationRef.current) {
        // A conversation is being created: this goes into it, after the
        // message that started it.
        creationRef.current.followUps.push(message);
      } else {
        void createConversation(message, companyIdTarget);
      }
    });
    },
    true,
    // Asked too long ago for the chat to send it now: said, not lost.
    (text) =>
      toastError({
        description: t("components.mobileShell.questionExpired", { text }),
      })
  );

  // Once the URL names the new conversation, its message belongs to it alone:
  // a welcome screen opened before it is sent must not show it as its own.
  useEffect(() => {
    // An updater, not the pending read at render: the conversation's own
    // effect, which runs first, may have sent the message and cleared it in
    // this same commit — writing the stale value back would send it again on
    // the next visit.
    setPending((current) =>
      current?.fromWelcome && current.conversationId === conversationId
        ? { ...current, fromWelcome: false }
        : current
    );
  }, [pending, conversationId]);

  // Left before its message went out — a conversation still loading, or
  // still answering — the message waits for the conversation, first in line,
  // rather than being dropped by the next one's.
  useEffect(() => {
    const current = pendingRef.current;
    if (
      !current?.conversationId ||
      current.fromWelcome ||
      current.conversationId === conversationId
    ) {
      return;
    }
    parkMessageFirst(current.conversationId, current.message);
    setPending(null);
  }, [conversationId]);

  // A conversation opened with messages still waiting for it sends them, once
  // it is on screen and one at a time: handed over at mount, a message would
  // read as the first of a conversation just created, and its history would
  // never load.
  useEffect(() => {
    if (!conversationId || !conversation || pending) return;
    const message = takeParkedMessage(conversationId);
    if (message) setPending({ message, conversationId });
  }, [conversationId, conversation, pending]);

  // The title is written once the first answer is in: refresh both views.
  const onMessageGenerated = () => {
    void queryClient.invalidateQueries({
      queryKey: conversationKey(teamId, conversationId),
    });
    void refreshConversations();
  };

  // A pending message belongs to one conversation, or to the welcome screen
  // while the conversation it starts is created.
  const isWelcomePending =
    !conversationId &&
    !!pending &&
    (pending.conversationId === null || !!pending.fromWelcome);
  const pendingHere =
    pending &&
    (isWelcomePending || pending.conversationId === conversationId)
      ? pending.message
      : null;
  const isCreatingHere = isWelcomePending;

  return (
    <div className="absolute inset-0">
      {/* Until the conversation is read: a message waiting for it is sent once
          it is on screen, never into the welcome screen its absence shows. */}
      {cannotBeRead ? (
        <Empty className="h-full">
          <EmptyHeader>
            <EmptyTitle>
              {t("components.mobileShell.conversationLoadError")}
            </EmptyTitle>
          </EmptyHeader>
          <EmptyContent>
            <Button
              className="h-11"
              disabled={isFetching}
              onClick={() => void refetch()}
            >
              {t("components.mobileShell.retry")}
            </Button>
            <Button
              variant="ghost"
              className="h-11"
              onClick={() => navigate("/chat")}
            >
              {t("components.mobileShell.newConversation")}
            </Button>
          </EmptyContent>
        </Empty>
      ) : conversationId && !conversation ? (
        <ChatConversationSkeleton />
      ) : (
        <ChatLayout
          conversation={conversationId ? conversation : undefined}
          pendingMessage={pendingHere}
          isCreatingConversation={isCreatingHere}
          isNewConversation={
            !!pending?.fromWelcome && pending.conversationId === conversationId
          }
          onStopCreatingConversation={stopCreating}
          draftToRestore={draftToRestore}
          onDraftRestored={() => setDraftToRestore(null)}
          triggerCreateNewConversation={createConversation}
          onMessageGenerated={onMessageGenerated}
          onPendingMessageSent={() => setPending(null)}
        />
      )}
    </div>
  );
}
